import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { endOfDay, format, isValid, parseISO, startOfDay, subDays } from 'date-fns';
import * as React from 'react';
import { useDebounceValue } from '../../../../../hooks/useDebounceValue';
import { AuditLogCategoryFilter, IAuditLogFilters } from '../../../../../queries/Admin/AuditLog/types';
import {
	AUDIT_LOG_RETENTION_DAYS,
	DEFAULT_AUDIT_LOG_LOOKBACK_DAYS,
	useAdminAccountAuditLogQuery,
} from '../../../../../queries/Admin/AuditLog/useAdminAccountAuditLogQuery';
import {
	getContactReferenceDisplayName,
	useAdminAccountContactAutocompleteQuery,
} from '../../../../../queries/Admin/AuditLog/useAdminAccountContactAutocompleteQuery';
import { Avatar } from '@HoudiniUI/Avatar';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { SearchIcon2 } from '../../../../../web/components/svgs/icons/SearchIcon2';
import { XIcon } from '../../../../../web/components/svgs/icons/XIcon';
import { Button } from '../../../../../web/components-v2/Button';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { DateInput } from '../../../../../web/components-v2/DateInput';
import { Pill } from '../../../../../web/components-v2/Pill/Pill';
import { TabToggle } from '../../../../../web/components-v2/TabToggle';
import { EventLogRow } from './EventLogRow';
import { styleSheet } from './styles';

interface Props {
	accountId: string;
}

/** True only when both dates are present, valid, and the start day is after the end day. */
export function isInvertedRange(startStr: string, endStr: string): boolean {
	if (!startStr || !endStr) {
		return false;
	}
	const start = parseISO(startStr);
	const end = parseISO(endStr);
	if (!isValid(start) || !isValid(end)) {
		return false;
	}
	return startOfDay(start) > startOfDay(end);
}

/** Local-day boundaries → UTC ISO instants, clamped to the [today-60d, today] retention window.
 *  Returns null until both ends are present, if either is unparseable, or if start is after end. */
export function toIsoRange(startStr: string, endStr: string): { startDate: string; endDate: string } | null {
	if (!startStr || !endStr) {
		return null;
	}
	const today = startOfDay(new Date());
	const floor = subDays(today, AUDIT_LOG_RETENTION_DAYS);
	let start = startOfDay(parseISO(startStr));
	let end = endOfDay(parseISO(endStr));
	if (!isValid(start) || !isValid(end)) {
		return null;
	}
	if (start < floor) start = floor;
	if (end > endOfDay(today)) end = endOfDay(today);
	if (start > end) {
		return null;
	}
	return { startDate: start.toISOString(), endDate: end.toISOString() };
}

/** True when both dates are present, valid, and in order, yet the whole range falls outside the
 *  retention window (so `toIsoRange` clamps it to empty). Lets the UI show a retention-specific
 *  message instead of the generic "select dates" prompt. */
export function isRangeOutsideRetention(startStr: string, endStr: string): boolean {
	if (!startStr || !endStr) {
		return false;
	}
	const start = parseISO(startStr);
	const end = parseISO(endStr);
	if (!isValid(start) || !isValid(end)) {
		return false;
	}
	// Inverted ranges get their own message; this only covers well-formed, in-order ranges.
	if (startOfDay(start) > startOfDay(end)) {
		return false;
	}
	return toIsoRange(startStr, endStr) === null;
}

const RANGE_ITEMS: { value: string; label: string }[] = [
	{ value: '7', label: '7 days' },
	{ value: '30', label: '30 days' },
	{ value: '60', label: `${AUDIT_LOG_RETENTION_DAYS} days` },
	{ value: 'custom', label: 'Custom' },
];

// 'Unknown' is intentionally excluded — it is a server-side catch-all, not a user-facing filter.
// The explicit value type makes a typo in any item value a compile error rather than a silent
// mismatch against the `v as AuditLogCategoryFilter | 'all'` cast in onValueChange.
const CATEGORY_ITEMS: { value: AuditLogCategoryFilter | 'all'; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'TagChanges', label: 'Tag changes' },
	{ value: 'ContactMutation', label: 'Contact mutations' },
];

/** Secondary line for a contact row — helps tell apart contacts that share a name. */
function getContactReferenceEmail(contact: Api.ContactReference): string {
	// `primaryEmail` is typed as `string` but the endpoint serializes it as an EmailAddress object
	// ({ value }), so normalize before use to avoid rendering an object as a React child.
	const primary = contact.primaryEmail as string | Api.EmailAddress | undefined;
	const primaryValue = typeof primary === 'string' ? primary : primary?.value;
	return primaryValue ?? contact.emailAddresses?.[0]?.value ?? '';
}

function getContactReferenceInitials(contact: Api.ContactReference): string {
	const initials = `${contact.firstName?.charAt(0) ?? ''}${contact.lastName?.charAt(0) ?? ''}`.trim();
	return (initials || getContactReferenceDisplayName(contact).charAt(0)).toUpperCase();
}

/**
 * Inline contact search input for the event-log filter. Searches the viewed account's contacts
 * (server-side, account-scoped) as the admin types and invokes onSelect once a contact is picked.
 * It's only mounted while no contact is selected — selecting one swaps it for the badge below — so it
 * never has to display a persisted selection (which Base UI's Combobox clears on select anyway).
 */
const ContactSearchCombobox: React.FC<{
	accountId: string;
	onSelect: (contact: Api.ContactReference) => void;
}> = ({ accountId, onSelect }) => {
	const [search, setSearch] = React.useState('');
	const debouncedSearch = useDebounceValue(search, 400);
	const isSearching = debouncedSearch.trim().length > 0;
	const contactsQuery = useAdminAccountContactAutocompleteQuery({
		accountId,
		fragment: debouncedSearch,
		enabled: isSearching,
		meta: { toastErrorMessage: 'Failed to search contacts' },
	});
	const contactOptions = React.useMemo(() => contactsQuery.data?.values ?? [], [contactsQuery.data]);

	let status: React.ReactNode = null;
	if (isSearching && contactsQuery.isLoading) {
		status = (
			<span className={css(styleSheet.contactStatus)}>
				<LoadingSpinner type='extra-small' data-testid='contact-search-loading' /> Searching contacts…
			</span>
		);
	} else if (isSearching && contactOptions.length === 0) {
		status = `No contacts found for "${debouncedSearch.trim()}"`;
	}

	return (
		<Combobox
			items={contactOptions}
			itemToStringLabel={item => getContactReferenceDisplayName(item as Api.ContactReference)}
			isItemEqualToValue={(a, b) => (a as Api.ContactReference)?.id === (b as Api.ContactReference)?.id}
			filter={null}
			onValueChange={value => {
				const contact = value as Api.ContactReference | null;
				if (contact) {
					onSelect(contact);
				}
			}}
			onInputValueChange={setSearch}
		>
			<ComboboxInput placeholder='Search by contact name' showTrigger={false} />
			<ComboboxContent styles={[styleSheet.contactDropdown]}>
				{status ? <ComboboxStatus>{status}</ComboboxStatus> : null}
				<ComboboxList>
					{(contact: Api.ContactReference) => {
						const displayName = getContactReferenceDisplayName(contact);
						const email = getContactReferenceEmail(contact);
						return (
							<ComboboxAvatarItem key={contact.id} value={contact}>
								<ComboboxAvatarItemAvatar
									imageUrl={contact.profilePic ?? undefined}
									fallbackText={getContactReferenceInitials(contact)}
								/>
								<ComboboxAvatarItemTitle>{displayName}</ComboboxAvatarItemTitle>
								{/* Skip the email line when it's already the title (contact has no name on file). */}
								<ComboboxAvatarItemDescription>
									{email && email !== displayName ? email : undefined}
								</ComboboxAvatarItemDescription>
							</ComboboxAvatarItem>
						);
					}}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

/** Filter bar + infinite-scroll table for the account event log. */
export const AccountEventLogTab: React.FC<Props> = ({ accountId }) => {
	const [category, setCategory] = React.useState<AuditLogCategoryFilter | 'all'>('all');
	const [selectedContact, setSelectedContact] = React.useState<Api.ContactReference | null>(null);
	const [lookbackDays, setLookbackDays] = React.useState<number>(DEFAULT_AUDIT_LOG_LOOKBACK_DAYS);
	const [isCustom, setIsCustom] = React.useState(false);
	const [startDateStr, setStartDateStr] = React.useState('');
	const [endDateStr, setEndDateStr] = React.useState('');

	const isoRange = isCustom ? toIsoRange(startDateStr, endDateStr) : null;
	const hasCompleteRange = isCustom && isoRange !== null;

	const filters: IAuditLogFilters = {
		lookbackDays,
		category: category === 'all' ? undefined : category,
		contactId: selectedContact?.id ?? undefined,
		startDate: isoRange?.startDate,
		endDate: isoRange?.endDate,
	};

	const auditLog = useAdminAccountAuditLogQuery({
		accountId,
		filters,
		enabled: !isCustom || hasCompleteRange,
		meta: { toastErrorMessage: 'Failed to load the event log' },
	});

	const entries = (auditLog.data?.pages ?? []).flatMap(page => page.values);

	const handleReset = () => {
		setCategory('all');
		setSelectedContact(null);
		setIsCustom(false);
		setLookbackDays(DEFAULT_AUDIT_LOG_LOOKBACK_DAYS);
		setStartDateStr('');
		setEndDateStr('');
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.card)}>
				<div className={css(styleSheet.cardHeader)}>
					{!(isCustom && !hasCompleteRange) ? (
						<Pill variant='label' color='gray' label={`${entries.length}${auditLog.hasNextPage ? '+' : ''} events`} />
					) : null}
					<span
						className={css(styleSheet.retentionNote)}
					>{`Events older than ${AUDIT_LOG_RETENTION_DAYS} days are not retained`}</span>
				</div>
				<div className={css(styleSheet.filterBar)}>
					<div className={css(styleSheet.filterField)}>
						<span className={css(styleSheet.filterLabel)}>Event type</span>
						<TabToggle
							items={CATEGORY_ITEMS}
							value={category}
							onValueChange={v => setCategory(v as AuditLogCategoryFilter | 'all')}
						/>
					</div>

					<div className={css(styleSheet.filterField)}>
						<span className={css(styleSheet.filterLabel)}>Time range</span>
						<TabToggle
							items={RANGE_ITEMS}
							value={isCustom ? 'custom' : String(lookbackDays)}
							onValueChange={v => {
								if (v === 'custom') {
									setIsCustom(true);
									// Default the custom range to the last 30 days → today so it queries immediately
									// instead of opening on the empty "select a date" prompt.
									setStartDateStr(format(subDays(new Date(), DEFAULT_AUDIT_LOG_LOOKBACK_DAYS), 'yyyy-MM-dd'));
									setEndDateStr(format(new Date(), 'yyyy-MM-dd'));
								} else {
									setIsCustom(false);
									setLookbackDays(Number(v));
									setStartDateStr('');
									setEndDateStr('');
								}
							}}
						/>
					</div>

					{isCustom ? (
						<>
							<div role='group' aria-label='Start date' className={css(styleSheet.filterField)}>
								<span className={css(styleSheet.filterLabel)}>Start date</span>
								<DateInput value={startDateStr} onChange={setStartDateStr} />
							</div>
							<div role='group' aria-label='End date' className={css(styleSheet.filterField)}>
								<span className={css(styleSheet.filterLabel)}>End date</span>
								<DateInput value={endDateStr} onChange={setEndDateStr} />
							</div>
						</>
					) : null}

					<label className={css(styleSheet.filterField, styleSheet.contactField)}>
						<span className={css(styleSheet.filterLabel)}>Contact</span>
						{selectedContact ? (
							// Once a contact is picked, show it as a removable badge (Base UI clears the search input
							// on selection, so the badge — not the input — carries the active filter).
							<div className={css(styleSheet.selectedContact)}>
								<Avatar
									imgSrc={selectedContact.profilePic ?? ''}
									fallbackText={getContactReferenceInitials(selectedContact)}
									styles={[styleSheet.selectedContactAvatar]}
								/>
								<span className={css(styleSheet.selectedContactName)}>
									{getContactReferenceDisplayName(selectedContact)}
								</span>
								<button
									type='button'
									aria-label={`Remove ${getContactReferenceDisplayName(selectedContact)}`}
									className={css(styleSheet.selectedContactRemove)}
									onClick={() => setSelectedContact(null)}
								>
									<XIcon width={10} height={10} />
								</button>
							</div>
						) : (
							<ContactSearchCombobox accountId={accountId} onSelect={setSelectedContact} />
						)}
					</label>

					<Button variant='ghost' onClick={handleReset}>
						Reset
					</Button>
				</div>

				{hasCompleteRange ? (
					<div className={css(styleSheet.dateRangeNote)}>
						Showing {format(parseISO(isoRange.startDate), 'MMM d')} –{' '}
						{format(parseISO(isoRange.endDate), 'MMM d, yyyy')}
					</div>
				) : null}

				{isCustom && !hasCompleteRange ? (
					<div className={css(styleSheet.stateMessage)}>
						{isInvertedRange(startDateStr, endDateStr)
							? 'End date must be on or after the start date.'
							: isRangeOutsideRetention(startDateStr, endDateStr)
								? `Selected range is outside the last ${AUDIT_LOG_RETENTION_DAYS} days.`
								: 'Select a start and end date to filter.'}
					</div>
				) : (
					<>
						{auditLog.isLoading ? (
							<div className={css(styleSheet.stateMessage, styleSheet.loadingState)}>
								<LoadingSpinner type='small' data-testid='event-log-loading' />
								Loading event log
							</div>
						) : auditLog.isError ? (
							<div className={css(styleSheet.stateMessage)}>Failed to load the event log.</div>
						) : entries.length === 0 ? (
							<div className={css(styleSheet.emptyState)}>
								<SearchIcon2 className={css(styleSheet.emptyStateIcon)} />
								<p className={css(styleSheet.emptyStateHeading)}>No events match your filters</p>
								<p className={css(styleSheet.emptyStateSubtext)}>Try widening the date range or clearing the search.</p>
							</div>
						) : (
							<>
								<table className={css(styleSheet.table)}>
									<thead>
										<tr className={css(styleSheet.columnHeaderRow)}>
											<th className={css(styleSheet.columnHeader, styleSheet.firstColumn)}>Timestamp</th>
											<th className={css(styleSheet.columnHeader)}>Actor</th>
											<th className={css(styleSheet.columnHeader)}>Action</th>
											<th className={css(styleSheet.columnHeader)}>Summary</th>
											<th className={css(styleSheet.columnHeader, styleSheet.lastColumn)} />
										</tr>
									</thead>
									<tbody>
										{entries.map(entry => (
											<EventLogRow key={entry.id} entry={entry} />
										))}
									</tbody>
								</table>
								{auditLog.hasNextPage ? (
									<div className={css(styleSheet.loadMoreWrapper)}>
										<Button
											variant='secondary'
											onClick={() => auditLog.fetchNextPage()}
											disabled={auditLog.isFetchingNextPage}
										>
											{auditLog.isFetchingNextPage ? 'Loading…' : 'Load more'}
										</Button>
									</div>
								) : null}
							</>
						)}
					</>
				)}
			</div>
		</div>
	);
};
